import { Avatar, Box, Icon, Image, useToken } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FaUser } from "react-icons/fa";
import { useIntl } from "react-intl";

import { ApiKeyIcon, BORDER_WIDTHS, RanaButton } from "@nens/ranalib";

import DeadFrog from "@/images/deadFrog.png";

import { ProfileModal } from "./components/profile-modal";
import { AnyUser } from "./types/any-user";
import { useProfile } from "./use-profile";

export const PROFILE_BORDER = BORDER_WIDTHS.thick;

export function Profile({
  user,
  noModal,
  inTable,
  boxSize = inTable ? 8 : 11,
  children,
}: {
  user?: "self" | AnyUser;
  noModal?: boolean;
  boxSize?: number;
  inTable?: boolean;
  children?: ReactNode;
}) {
  const intl = useIntl();
  const { userInitials, appliedUser, userImage, isDeleted, isApiKeyUser } = useProfile(user);
  const imageUrl = isDeleted ? DeadFrog : userImage;

  const hasModal = !noModal && appliedUser && !isDeleted;
  const [boxSizeString] = useToken("sizes", [boxSize]);

  // If no user, show generic icon
  const notAppliedUserIcon = !appliedUser ? (
    <Icon as={FaUser} boxSize={`calc(${boxSizeString} * 0.7)`} color="brand.keep_analytic" />
  ) : null;
  // If user is apikeyuser
  const apiKeyUserIcon = isApiKeyUser ? (
    <Icon as={ApiKeyIcon} boxSize={`calc(${boxSizeString} * 0.8)`} color="brand.keep_analytic" />
  ) : null;
  // If there is an image, show the image
  const imageIcon = imageUrl ? (
    <Image objectFit="cover" h={isDeleted ? "4/6" : "full"} src={imageUrl} />
  ) : null;
  // Fallback to initials if no image is available
  const initialsIcon = <Box fontWeight={600}>{userInitials}</Box>;

  const icon = notAppliedUserIcon || apiKeyUserIcon || imageIcon || initialsIcon;

  const ShowAvatar = () => (
    <RanaButton
      variant="fourthiary"
      flexDir="column"
      alignItems="center"
      as={hasModal ? undefined : "div"}
      minW={0}
    >
      <Avatar
        title={`${appliedUser?.givenName} ${appliedUser?.familyName}`}
        fontSize={boxSize ? Math.floor(boxSize * 1.7) : undefined}
        boxSize={boxSize}
        bg="brand.glacier"
        color="brand.analytic"
        borderColor="brand.sky"
        borderWidth={PROFILE_BORDER}
        overflow="hidden"
        position="relative"
        icon={icon}
      />
      {children}
    </RanaButton>
  );

  return hasModal ? (
    <ProfileModal user={appliedUser}>
      <ShowAvatar />
    </ProfileModal>
  ) : (
    <Box
      title={
        isDeleted
          ? intl.formatMessage({
              id: "user.deleted",
              defaultMessage: "User deleted",
            })
          : undefined
      }
    >
      <ShowAvatar />
    </Box>
  );
}
